import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Monitor, Wifi, WifiOff, Tv, Radio, Zap, Moon, Clock, Timer,
  Play, Pause, SkipBack, Volume2, Image, FileText, Youtube,
  BarChart3, Type, QrCode, Film, Hourglass, Video, Camera,
  LayoutGrid, AlertCircle, ExternalLink, X
} from "lucide-react";
import { useBroadcastSlave } from "@/hooks/useBroadcastSync";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_ICONS = {
  livestats: BarChart3, image: Image, pdf: FileText, youtube: Youtube,
  video_url: Film, local_video: Film, text: Type, qr: QrCode, wait: Clock, black: Moon, countdown: Hourglass,
  livekit_speakers: Video, camera: Camera, scene: LayoutGrid, ip_camera: Radio,
};

/**
 * DetachedPanelPage — A detached window that shows a single panel from the Regie.
 * Communicates with the master window via BroadcastChannel.
 * 
 * Route: /detached-panel/:sessionId?panel=preview|program|sources
 * 
 * IMPORTANT: This window does NOT access cameras or WebRTC.
 * Camera/LiveKit content shows a placeholder with status indicator.
 */
export default function DetachedPanelPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const panelType = searchParams.get("panel") || "program";
  const { state, connected, sendCommand } = useBroadcastSlave(sessionId, panelType);

  const panelTitles = {
    preview: "PREVIEW",
    program: "PROGRAM — En direct",
    sources: "Sources & Scenes",
  };

  if (!connected || !state) {
    return (
      <div className="w-screen h-screen bg-[#060B14] flex items-center justify-center" data-testid="detached-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#0099CC]/30 border-t-[#0099CC] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-sm font-medium">Connexion a la regie...</p>
          <p className="text-[#64748b] text-xs mt-2">Session: {sessionId}</p>
          <p className="text-[#64748b] text-xs">Panneau: {panelTitles[panelType] || panelType}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-[#060B14] flex flex-col overflow-hidden select-none" data-testid="detached-panel-page">
      {/* Top bar */}
      <div className="bg-[#060B14] border-b border-[#1A2332] px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <ExternalLink className="w-4 h-4 text-[#0099CC]" />
          <span className="text-white font-bold text-sm">{panelTitles[panelType] || panelType}</span>
          <span className="text-[#64748B] text-xs">— Fenetre detachee</span>
        </div>
        <div className="flex items-center gap-3">
          {state.wsConnected
            ? <Wifi className="w-3.5 h-3.5 text-green-400" />
            : <WifiOff className="w-3.5 h-3.5 text-red-400" />
          }
          <div className="flex items-center gap-1.5 bg-[#0B1120] px-2.5 py-1 rounded-lg border border-[#1A2332]">
            <Tv className="w-3.5 h-3.5 text-green-400" />
            <span className="text-xs text-white">{state.displayCount || 0} ecran(s)</span>
          </div>
        </div>
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-hidden">
        {panelType === "preview" && (
          <PreviewPanel state={state} sendCommand={sendCommand} />
        )}
        {panelType === "program" && (
          <ProgramPanel state={state} sendCommand={sendCommand} />
        )}
        {panelType === "sources" && (
          <SourcesPanel state={state} sendCommand={sendCommand} />
        )}
      </div>
    </div>
  );
}

// ===== PREVIEW PANEL =====
function PreviewPanel({ state, sendCommand }) {
  return (
    <div className="w-full h-full flex flex-col p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2.5 h-2.5 rounded-full bg-[#0099CC]" />
        <span className="text-sm text-[#0099CC] uppercase tracking-widest font-bold">Preview</span>
      </div>
      <div className="flex-1 bg-black rounded-xl overflow-hidden border-2 border-[#0099CC]/30 relative" data-testid="detached-preview-monitor">
        <DetachedContentRenderer content={state.preview} isPreview />
        {state.preview?.name && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-4 py-2">
            <p className="text-white text-sm">{state.preview.name}</p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-3 mt-3">
        <Button onClick={() => sendCommand("go_black")} className="bg-[#1A2332] hover:bg-[#2A3441] text-white h-9 text-xs" data-testid="detached-btn-black">
          <Moon className="w-3.5 h-3.5 mr-1" /> ECRAN NOIR
        </Button>
        <Button onClick={() => sendCommand("go_live")} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 h-10 text-sm shadow-lg shadow-red-600/30" data-testid="detached-btn-live">
          <Zap className="w-4 h-4 mr-2" /> DIFFUSER
        </Button>
      </div>
    </div>
  );
}

// ===== PROGRAM PANEL =====
function ProgramPanel({ state }) {
  return (
    <div className="w-full h-full flex flex-col p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm text-red-400 uppercase tracking-widest font-bold">Program — En direct</span>
        {state.timerElapsed > 0 && (
          <span className="ml-auto flex items-center gap-1 text-sm font-mono tabular-nums text-red-400/80 bg-red-500/10 px-3 py-1 rounded">
            <Timer className="w-3.5 h-3.5" />
            {String(Math.floor(state.timerElapsed / 60)).padStart(2, "0")}:{String(state.timerElapsed % 60).padStart(2, "0")}
          </span>
        )}
      </div>
      <div className="flex-1 bg-black rounded-xl overflow-hidden border-2 border-red-500/30 relative" data-testid="detached-program-monitor">
        <DetachedContentRenderer content={state.program} />
        {state.program?.name && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-4 py-2">
            <p className="text-white text-sm">{state.program.name}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== SOURCES PANEL =====
function SourcesPanel({ state, sendCommand }) {
  return (
    <div className="w-full h-full flex flex-col p-4 overflow-y-auto">
      <div className="mb-4">
        <span className="text-xs text-[#64748B] uppercase tracking-widest font-bold">Sources</span>
      </div>
      <div className="space-y-1 mb-6">
        {(state.sources || []).map((src, i) => {
          const Icon = SOURCE_ICONS[src.type] || Image;
          const isPreview = state.preview?.type === src.type;
          const isProgram = state.program?.type === src.type;
          return (
            <div
              key={src.id}
              onClick={() => sendCommand("select_source", { sourceIndex: i })}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all text-sm ${
                isPreview ? "bg-[#0099CC]/20 border border-[#0099CC]/40" :
                isProgram ? "bg-red-500/10 border border-red-500/30" :
                "hover:bg-[#0B1120] border border-transparent"
              }`}
              data-testid={`detached-source-${i}`}
            >
              <span className="text-[#64748B] w-5 text-center font-mono">{i + 1}</span>
              <Icon className={`w-4 h-4 flex-shrink-0 ${isProgram ? "text-red-400" : isPreview ? "text-[#0099CC]" : "text-[#64748B]"}`} />
              <span className={`truncate flex-1 ${isProgram ? "text-red-400" : isPreview ? "text-[#0099CC]" : "text-[#94A3B8]"}`}>
                {src.name}
              </span>
              {isProgram && <span className="text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded font-bold">LIVE</span>}
            </div>
          );
        })}
      </div>

      {(state.scenes || []).length > 0 && (
        <>
          <div className="mb-3">
            <span className="text-xs text-[#64748B] uppercase tracking-widest font-bold">Scenes</span>
          </div>
          <div className="space-y-1">
            {state.scenes.map((scene, i) => (
              <div
                key={scene.id}
                onClick={() => sendCommand("load_scene", { sceneIndex: i })}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#0B1120] cursor-pointer transition-all text-sm border border-transparent"
                data-testid={`detached-scene-${i}`}
              >
                {i < 9 && (
                  <kbd className="text-[9px] font-mono bg-[#1A2332] text-[#F97316] px-1.5 py-0.5 rounded border border-[#2A3441]">F{i + 1}</kbd>
                )}
                <span className="text-[#94A3B8] truncate flex-1">{scene.name}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-auto pt-4 flex items-center justify-center gap-3">
        <Button onClick={() => sendCommand("go_black")} className="bg-[#1A2332] hover:bg-[#2A3441] text-white h-9 text-xs" data-testid="detached-src-btn-black">
          <Moon className="w-3.5 h-3.5 mr-1" /> ECRAN NOIR
        </Button>
        <Button onClick={() => sendCommand("go_live")} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 h-10 text-sm shadow-lg shadow-red-600/30" data-testid="detached-src-btn-live">
          <Zap className="w-4 h-4 mr-2" /> DIFFUSER
        </Button>
      </div>
    </div>
  );
}

// ===== DETACHED CONTENT RENDERER =====
// This renderer handles serializable content only.
// Camera/LiveKit streams are NOT available — show placeholder.
function DetachedContentRenderer({ content, isPreview }) {
  if (!content) return <div className="w-full h-full bg-black" />;

  if (content.type === "black") {
    return <div className="w-full h-full bg-black flex items-center justify-center"><Moon className="w-10 h-10 text-white/10" /></div>;
  }

  if (content.type === "wait") {
    return (
      <div className="w-full h-full flex items-center justify-center p-6" style={{ background: `linear-gradient(135deg, ${content.config?.bg_color || "#0B1120"}, ${content.config?.bg_end || "#1a1a2e"})` }}>
        <div className="text-center">
          {content.config?.logo_url && <img src={content.config.logo_url} alt="" className="h-12 mx-auto mb-4 object-contain" />}
          <div className="w-10 h-10 mx-auto border-2 border-white/20 border-t-white/60 rounded-full animate-spin mb-4" />
          <p className="text-white/70 text-sm">{content.message || content.config?.message || "En attente..."}</p>
        </div>
      </div>
    );
  }

  if (content.type === "image" && content.config?.url) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <img src={content.config.url} alt="" className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  if (content.type === "pdf" && content.config?.full_url) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <img src={content.config.full_url} alt="" className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  if (content.type === "youtube") {
    const videoId = content.config?.video_id;
    if (videoId && !isPreview) {
      return (
        <div className="w-full h-full bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&rel=0`}
            className="w-full h-full"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="YouTube"
          />
        </div>
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <div className="text-center">
          <Youtube className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-white text-sm">{content.name || "YouTube"}</p>
        </div>
      </div>
    );
  }

  if (content.type === "text") {
    return (
      <div className="w-full h-full flex items-center justify-center p-6" style={{ backgroundColor: content.config?.bg_color || "#0B1120" }}>
        <p className="text-center text-base" style={{ color: content.config?.color || "#fff" }}>{content.config?.text}</p>
      </div>
    );
  }

  if (content.type === "ip_camera" && content.config?.url) {
    return <DetachedIpCameraPlayer url={content.config.url} format={content.config.format} isPreview={isPreview} />;
  }

  // Camera / LiveKit — cannot stream in detached window (no WebRTC access)
  if (content.type === "camera" || content.type === "livekit_speakers" || content.type === "scene") {
    const typeLabels = { camera: "Camera USB", livekit_speakers: "Intervenants Live", scene: "Scene" };
    const TypeIcon = SOURCE_ICONS[content.type] || Video;
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
        <div className="text-center px-6">
          <TypeIcon className="w-10 h-10 text-[#0099CC]/40 mx-auto mb-3" />
          <p className="text-white text-sm font-medium">{content.name || typeLabels[content.type]}</p>
          <p className="text-[#64748b] text-xs mt-2">
            Le flux {content.type === "camera" ? "camera" : "video"} est visible sur la fenetre principale.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 bg-[#0099CC]/10 text-[#0099CC] text-[10px] px-3 py-1.5 rounded-full">
            <Monitor className="w-3 h-3" />
            Flux en cours sur la regie
          </div>
        </div>
      </div>
    );
  }

  if (content.type === "livestats") {
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#0B1120] to-[#0d1a2d] flex items-center justify-center">
        <div className="text-center"><BarChart3 className="w-12 h-12 text-[#0099CC] mx-auto mb-3" /><p className="text-white text-sm">Live Stats</p></div>
      </div>
    );
  }

  if (content.type === "countdown") {
    return <DetachedCountdown config={content.config} />;
  }

  return <div className="w-full h-full bg-black" />;
}

// IP Camera player for detached window (HLS/direct — works fine since it's URL-based)
function DetachedIpCameraPlayer({ url, format, isPreview }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || !videoRef.current) return;
    setError(null);
    const video = videoRef.current;
    const detectedFormat = format === "auto" || !format
      ? (url.toLowerCase().includes(".m3u8") ? "hls" : "direct")
      : format;

    if (detectedFormat === "hls") {
      import("hls.js").then(({ default: Hls }) => {
        if (!Hls.isSupported()) {
          video.src = url;
          video.play().catch(() => {});
          return;
        }
        if (hlsRef.current) hlsRef.current.destroy();
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) setError(`Erreur HLS: ${data.details}`);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
      }).catch(() => setError("Impossible de charger hls.js"));
    } else {
      video.src = url;
      video.play().catch(() => {});
      video.onerror = () => setError("Flux video inaccessible");
    }

    return () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      video.src = "";
    };
  }, [url, format]);

  if (error) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <div className="text-center px-4">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-black relative">
      <video ref={videoRef} autoPlay playsInline muted={isPreview} className="w-full h-full object-contain" data-testid="detached-ip-camera-video" />
      <div className="absolute top-2 left-2 bg-[#F97316] text-black text-[8px] font-bold px-2 py-0.5 rounded">IP CAM</div>
    </div>
  );
}

function DetachedCountdown({ config }) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!config?.target_time) return;
    const target = new Date(config.target_time).getTime();
    const tick = () => setRemaining(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [config?.target_time]);

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B0F19] to-[#151B2B] flex items-center justify-center">
      <div className="text-center">
        {config?.label && <div className="text-xl text-[#94A3B8] mb-4">{config.label}</div>}
        <div className="text-7xl font-mono font-bold text-white tracking-wider">
          {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </div>
        {remaining <= 0 && config?.end_message && (
          <div className="text-2xl text-[#10B981] mt-4 animate-pulse">{config.end_message}</div>
        )}
      </div>
    </div>
  );
}
